import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { productApi, Product } from '../services/api';
import './Navbar.css';

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);
  const [isAboutDropdownOpen, setIsAboutDropdownOpen] = useState(false);
  const [productCategories, setProductCategories] = useState<Array<{ name: string; path: string }>>([]);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const aboutDropdownRef = useRef<HTMLDivElement>(null);
  const productsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const aboutTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isActive = (path: string) => location.pathname === path;

  // 获取产品分类
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await productApi.getAll(true);
        const products: Product[] = response.data;
        
        // 提取所有唯一的分类
        const categorySet = new Set<string>();
        products.forEach(product => {
          if (product.category && product.category.trim()) {
            categorySet.add(product.category.trim());
          }
        });
        
        // 构建分类列表，包含"All Products"
        const categories = [
          { name: 'All Products', path: '/products' },
          ...Array.from(categorySet).map(category => ({
            name: category,
            path: `/products?category=${encodeURIComponent(category)}`
          }))
        ];
        
        setProductCategories(categories);
      } catch (error) {
        console.error('Error fetching product categories:', error);
        // 如果获取失败，至少显示"All Products"
        setProductCategories([{ name: 'All Products', path: '/products' }]);
      }
    };

    fetchCategories();
  }, []);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (productsTimeoutRef.current) {
        clearTimeout(productsTimeoutRef.current);
      }
      if (aboutTimeoutRef.current) {
        clearTimeout(aboutTimeoutRef.current);
      }
    };
  }, []);

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/" className="navbar-logo">
          <img src="/logo.png" alt="UP" className="logo-img" />
          <span className="logo-subtitle">Hotel Suppliers</span>
        </Link>
        
        <div className={`navbar-menu ${isMenuOpen ? 'active' : ''}`}>
          <Link 
            to="/" 
            className={`navbar-link ${isActive('/') ? 'active' : ''}`}
            onClick={() => setIsMenuOpen(false)}
          >
            Home
          </Link>
          <div 
            className="navbar-dropdown"
            ref={dropdownRef}
            onMouseEnter={() => {
              if (productsTimeoutRef.current) {
                clearTimeout(productsTimeoutRef.current);
                productsTimeoutRef.current = null;
              }
              setIsProductsDropdownOpen(true);
            }}
            onMouseLeave={() => {
              productsTimeoutRef.current = setTimeout(() => {
                setIsProductsDropdownOpen(false);
              }, 200);
            }}
          >
            <Link
              to="/products"
              className={`navbar-link dropdown-toggle ${isActive('/products') ? 'active' : ''}`}
            >
              Products
              <span className={`dropdown-arrow ${isProductsDropdownOpen ? 'open' : ''}`}>▼</span>
            </Link>
            {isProductsDropdownOpen && (
              <div 
                className="dropdown-menu"
                onMouseEnter={() => {
                  if (productsTimeoutRef.current) {
                    clearTimeout(productsTimeoutRef.current);
                    productsTimeoutRef.current = null;
                  }
                  setIsProductsDropdownOpen(true);
                }}
                onMouseLeave={() => {
                  productsTimeoutRef.current = setTimeout(() => {
                    setIsProductsDropdownOpen(false);
                  }, 200);
                }}
              >
                {productCategories.map((category, index) => (
                  <Link
                    key={index}
                    to={category.path}
                    className="dropdown-item"
                    onClick={() => {
                      setIsMenuOpen(false);
                    }}
                  >
                    {category.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <div 
            className="navbar-dropdown"
            ref={aboutDropdownRef}
            onMouseEnter={() => {
              if (aboutTimeoutRef.current) {
                clearTimeout(aboutTimeoutRef.current);
                aboutTimeoutRef.current = null;
              }
              setIsAboutDropdownOpen(true);
            }}
            onMouseLeave={() => {
              aboutTimeoutRef.current = setTimeout(() => {
                setIsAboutDropdownOpen(false);
              }, 200);
            }}
          >
            <Link
              to="/about"
              className={`navbar-link dropdown-toggle ${isActive('/about') ? 'active' : ''}`}
            >
              About Us
              <span className={`dropdown-arrow ${isAboutDropdownOpen ? 'open' : ''}`}>▼</span>
            </Link>
            {isAboutDropdownOpen && (
              <div 
                className="dropdown-menu"
                onMouseEnter={() => {
                  if (aboutTimeoutRef.current) {
                    clearTimeout(aboutTimeoutRef.current);
                    aboutTimeoutRef.current = null;
                  }
                  setIsAboutDropdownOpen(true);
                }}
                onMouseLeave={() => {
                  aboutTimeoutRef.current = setTimeout(() => {
                    setIsAboutDropdownOpen(false);
                  }, 200);
                }}
              >
                <Link
                  to="/about#company-overview"
                  className="dropdown-item"
                  onClick={() => {
                    setIsMenuOpen(false);
                  }}
                >
                  Company Overview
                </Link>
                <Link
                  to="/about#our-operation"
                  className="dropdown-item"
                  onClick={() => {
                    setIsMenuOpen(false);
                  }}
                >
                  Our Operation
                </Link>
                <Link
                  to="/about#faq"
                  className="dropdown-item"
                  onClick={() => {
                    setIsMenuOpen(false);
                  }}
                >
                  FAQ
                </Link>
              </div>
            )}
          </div>
          <Link 
            to="/news" 
            className={`navbar-link ${isActive('/news') ? 'active' : ''}`}
            onClick={() => setIsMenuOpen(false)}
          >
            News
          </Link>
          <Link 
            to="/contact" 
            className={`navbar-link ${isActive('/contact') ? 'active' : ''}`}
            onClick={() => setIsMenuOpen(false)}
          >
            Contact
          </Link>
        </div>

        <div 
          className="navbar-toggle"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
